import * as Sentry from '@sentry/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '../axios.js';
import { extractErrorMessage } from '../lib/error.js';
import type { Tag } from '../types/tag.js';

const fetchTagSuggestions = async ({
  query,
  limit,
}: {
  query: string;
  limit: number;
}): Promise<Tag[]> => {
  try {
    const { data } = await apiClient.get<Tag[]>('/tags/autocomplete', {
      params: { q: query, limit },
    });
    return data;
  } catch (error) {
    Sentry.captureException(error);
    throw new Error(extractErrorMessage(error, 'Failed to fetch tag suggestions'));
  }
};

const createTag = async ({ name }: { name: string }): Promise<Tag> => {
  try {
    const { data } = await apiClient.post<Tag>('/tags', { name });
    return data;
  } catch (error) {
    Sentry.captureException(error);
    throw new Error(extractErrorMessage(error, 'Failed to create tag'));
  }
};

export const useTagAutocomplete = (query: string, limit = 10, enabled: boolean = true) => {
  return useQuery<Tag[]>({
    queryKey: ['tag-autocomplete', query, limit],
    queryFn: () => fetchTagSuggestions({ query, limit }),
    enabled,
    staleTime: 30_000,
  });
};

export const useCreateTag = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createTag,
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) && query.queryKey[0] === 'tag-autocomplete',
      });
    },
  });
};
